import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, BookOpen, Heart, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { MINISTRY } from "@/data/site";
import GoldCross from "@/components/GoldCross";
import Reveal from "@/components/Reveal";
import VideoCard from "@/components/VideoCard";
import ProductCard from "@/components/ProductCard";
import PromoCarousel from "@/components/PromoCarousel";
import Seo from "@/components/Seo";

const Hero = () => (
  <section className="relative ivory-gold-bg overflow-hidden" data-testid="hero-section">
    <div className="god-rays absolute inset-0" />
    <GoldCross className="absolute left-1/4 top-10 h-[120%] w-80 hidden lg:block animate-glow-pulse" opacity={0.06} />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
      {/* Image left */}
      <Reveal className="order-2 lg:order-1">
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-gold/30 to-gold-light/10 blur-2xl" />
          <div className="relative rounded-[1.8rem] overflow-hidden border border-gold/30 shadow-2xl shadow-brown-dark/20 bg-gradient-to-b from-[#FBF3DE] to-[#F3E4BC]">
            <img
              src={MINISTRY.portrait}
              alt="Prophète Steeve Orphôss"
              loading="eager"
              fetchPriority="high"
              className="w-full h-[460px] sm:h-[600px] object-contain object-center"
              data-testid="hero-portrait"
            />
          </div>
        </div>
      </Reveal>

      {/* Text right */}
      <Reveal delay={0.15} className="order-1 lg:order-2 text-center lg:text-left">
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-6">
          <Sparkles size={14} /> Ministère International
        </p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight text-brown-black">
          PROPHÈTE
          <span className="block gold-gradient-text font-medium">STEEVE ORPHÔSS</span>
        </h1>
        <p className="mt-6 text-base md:text-lg text-brown-black/70 font-medium">
          Chef de chantre depuis 2003 · Auteur — Compositeur — Interprète — Producteur audiovisuel
        </p>
        <p className="mt-5 font-heading text-2xl md:text-3xl italic text-brown-dark leading-snug">
          « Un message pour cette génération, <br className="hidden sm:block" />
          une voix prophétique pour les nations »
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
          <Link to="/videos" data-testid="hero-watch-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gold text-white font-semibold shadow-lg shadow-gold/25 hover:bg-gold-light transition-all duration-300">
            <Play size={17} fill="currentColor" /> Regarder les messages
          </Link>
          <Link to="/videos" data-testid="hero-teachings-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-gold text-gold font-semibold hover:bg-gold/10 transition-all duration-300">
            <BookOpen size={17} /> Accéder aux enseignements
          </Link>
          <Link to="/faire-un-don" data-testid="hero-support-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brown-dark text-ivory font-semibold hover:bg-brown-black transition-all duration-300">
            <Heart size={16} fill="currentColor" /> Soutenir l'œuvre
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const AboutPreview = () => (
  <section className="py-20 md:py-28 bg-ivory">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4">Serviteur de Dieu</p>
        <h2 className="font-heading text-4xl md:text-5xl font-light text-brown-black tracking-tight">
          À propos de Prophète Steeve Orphôss
        </h2>
        <div className="gold-line w-24 mx-auto my-8" />
        <p className="text-lg text-brown-black/75 leading-relaxed max-w-3xl mx-auto">
          Prophète, auteur-compositeur et chantre depuis 2003, le Prophète Steeve Orphôss est un serviteur de Dieu
          passionné par la proclamation de l'Évangile et l'édification du Corps du Christ. À travers ses messages
          prophétiques, ses chants inspirés et ses enseignements puissants, il touche des vies et impacte des nations.
        </p>
        <Link to="/a-propos" data-testid="home-about-button" className="inline-flex items-center gap-2 mt-9 px-6 py-3 rounded-full border border-gold text-gold font-semibold hover:bg-gold hover:text-white transition-all duration-300">
          En savoir plus <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const SectionHeader = ({ overline, title, link, linkLabel }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-3">{overline}</p>
      <h2 className="font-heading text-4xl md:text-5xl font-light text-brown-black tracking-tight">{title}</h2>
    </div>
    {link && (
      <Link to={link} className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all duration-300 whitespace-nowrap">
        {linkLabel} <ArrowRight size={16} />
      </Link>
    )}
  </div>
);

const Home = () => {
  const [videos, setVideos] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data.slice(0, 3))).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="Ministère Chrétien International"
        description="Site officiel du Prophète Steeve Orphôss (Stezo l'Impériale) — Ministère chrétien international. Enseignements bibliques, messages prophétiques, louange, livres et ressources spirituelles. Auteur chrétien, designer IA et entrepreneur digital."
        path="/"
      />
      <PromoCarousel />
      <Hero />
      <AboutPreview />

      {/* Videos */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#FBF6E9] to-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader overline="Messages inspirés" title="Vidéos récentes" link="/videos" linkLabel="Voir toutes les vidéos" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {videos.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.1}>
                <VideoCard video={v} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Shop */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader overline="Ressources spirituelles" title="Boutique" link="/boutique" linkLabel="Voir toute la boutique" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Donation block */}
      <section className="py-16 md:py-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-brown-dark px-8 py-16 md:px-16 md:py-20" data-testid="home-donation-block">
              <div className="god-rays absolute inset-0 opacity-60" />
              <GoldCross className="absolute right-6 top-1/2 -translate-y-1/2 h-[150%] w-72 animate-glow-pulse" opacity={0.12} />
              <div className="relative max-w-2xl">
                <p className="text-xs uppercase tracking-[0.3em] text-gold-light font-semibold mb-4">Partenariat & Soutien</p>
                <h2 className="font-heading text-4xl md:text-5xl font-light text-ivory tracking-tight">Soutenez l'œuvre</h2>
                <p className="mt-6 text-lg text-ivory/75 leading-relaxed">
                  Votre soutien nous permet d'annoncer l'Évangile, de produire des contenus et d'impacter des vies à
                  travers le monde entier.
                </p>
                <Link to="/faire-un-don" data-testid="home-donate-now-button" className="inline-flex items-center gap-2 mt-9 px-7 py-3.5 rounded-full bg-gold text-white font-semibold shadow-lg shadow-gold/30 hover:bg-gold-light transition-all duration-300">
                  <Heart size={17} fill="currentColor" /> Faire un don maintenant
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
